/**
 * AI Accuracy Tab
 * Admin-only view for monitoring AI performance and calibration
 */

import { LoadingSpinner } from '@components/ui';
import { useAuth } from '@contexts/AuthContext';
import { formatAmericanOdds, MISSING_ODDS } from '@parlay-picker/shared/oddsUtils';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { PicksFilters } from '@server/services/admin/adminPicksTypes.ts';
import { AlertTriangle, CheckCircle, TrendingUp, XCircle } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../../config/api';

// =============================================================================
// Types
// =============================================================================

interface CalibrationBucket {
  recommendation: string; // 'BET', 'LEAN_BET', 'SKIP', 'CAUTION'
  picks: number;
  actualHitRate: number;
  expectedHitRate: number;
  deviation: number;
  calibrated: boolean;
}

interface DataQualityIssue {
  type: string;
  count: number;
  description: string;
}

interface AIAccuracyData {
  overview: {
    totalPicks: number;
    settledPicks: number;
    overallHitRate: number;
    lastUpdated: string;
  };
  dataQuality: {
    issues: DataQualityIssue[];
    healthScore: number;
  };
  calibration: {
    prediction: CalibrationBucket[];
    strategy: CalibrationBucket[];
    overallStatus: 'good' | 'moderate' | 'poor' | 'insufficient_data';
  };
  trends: Array<{
    period: string;
    picks: number;
    hitRate: number;
    roi: number;
  }>;
  breakdowns: {
    byPropType: Array<{
      propType: string;
      picks: number;
      hitRate: number;
      avgOdds: number;
    }>;
    byAnalysisMode: Array<{ mode: string; picks: number; hitRate: number }>;
    byConfidence: Array<{ range: string; picks: number; hitRate: number }>;
  };
}

// =============================================================================
// Helpers
// =============================================================================

function formatRecommendation(rec: string): string {
  switch (rec) {
    case 'BET':
      return 'BET';
    case 'LEAN_BET':
      return 'LEAN BET';
    case 'SKIP':
      return 'SKIP';
    case 'CAUTION':
      return 'CAUTION';
    default:
      return rec;
  }
}

// =============================================================================
// Sub-Components
// =============================================================================

function CalibrationTable({ buckets }: { buckets: CalibrationBucket[] }): ReactElement {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-theme-border">
            <th className="text-left py-2 px-4 text-theme-text-secondary">Recommendation</th>
            <th className="text-right py-2 px-4 text-theme-text-secondary">Picks</th>
            <th className="text-right py-2 px-4 text-theme-text-secondary">Expected</th>
            <th className="text-right py-2 px-4 text-theme-text-secondary">Actual</th>
            <th className="text-right py-2 px-4 text-theme-text-secondary">Deviation</th>
            <th className="text-center py-2 px-4 text-theme-text-secondary">Calibrated?</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket, idx) => (
            <tr key={idx} className="border-b border-theme-border hover:bg-theme-bg-secondary">
              <td className="py-2 px-4 text-theme-text font-semibold">
                {formatRecommendation(bucket.recommendation)}
              </td>
              <td className="text-right py-2 px-4 text-theme-text">{bucket.picks}</td>
              <td className="text-right py-2 px-4 text-theme-text">
                {(bucket.expectedHitRate * 100).toFixed(0)}%
              </td>
              <td className="text-right py-2 px-4 text-theme-text">
                {(bucket.actualHitRate * 100).toFixed(1)}%
              </td>
              <td className="text-right py-2 px-4 text-theme-text">
                {(bucket.deviation * 100).toFixed(1)}%
              </td>
              <td className="text-center py-2 px-4">
                {bucket.calibrated ? (
                  <CheckCircle className="w-5 h-5 text-theme-success inline-block" />
                ) : (
                  <XCircle className="w-5 h-5 text-theme-danger inline-block" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

interface AIAccuracyTabProps {
  /** Opens the Picks Data tab, narrowed by these filters when given. */
  onOpenPicksData: (filters?: PicksFilters) => void;
}

export function AIAccuracyTab({ onOpenPicksData }: AIAccuracyTabProps): ReactElement {
  const { authFetch } = useAuth();
  const [data, setData] = useState<AIAccuracyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl('/api/admin/ai-accuracy'));
      if (!response.ok) {
        throw new Error('Failed to fetch AI accuracy data');
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-theme-danger">{error || 'Failed to load AI accuracy data'}</p>
        <button
          type="button"
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const statusConfig = {
    good: { icon: CheckCircle, color: 'text-theme-success', label: 'Good Calibration' },
    moderate: { icon: AlertTriangle, color: 'text-theme-warning', label: 'Moderate Calibration' },
    poor: { icon: XCircle, color: 'text-theme-danger', label: 'Poor Calibration' },
    insufficient_data: {
      icon: AlertTriangle,
      color: 'text-theme-text-tertiary',
      label: 'Insufficient Data',
    },
  };

  const statusInfo = statusConfig[data.calibration.overallStatus];
  const StatusIcon = statusInfo.icon;

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-theme-text text-center">
          AI Accuracy & Calibration
        </h2>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={fetchData}
            className="px-4 py-2 bg-theme-bg text-theme-text rounded-lg hover:bg-theme-bg-secondary transition-colors flex items-center gap-2"
          >
            <TrendingUp className="w-4 h-4" />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="flex flex-wrap justify-center gap-4">
        <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border min-w-[160px]">
          <p className="text-sm text-theme-text-secondary mb-1">Total Picks</p>
          <p className="text-2xl font-bold text-theme-text">
            {data.overview.totalPicks.toLocaleString()}
          </p>
        </div>
        <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border min-w-[160px]">
          <p className="text-sm text-theme-text-secondary mb-1">Settled Picks</p>
          <p className="text-2xl font-bold text-theme-text">
            {data.overview.settledPicks.toLocaleString()}
          </p>
        </div>
        <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border min-w-[160px]">
          <p className="text-sm text-theme-text-secondary mb-1">Overall Hit Rate</p>
          <p className="text-2xl font-bold text-theme-text">
            {(data.overview.overallHitRate * 100).toFixed(1)}%
          </p>
        </div>
        <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border min-w-[160px]">
          <p className="text-sm text-theme-text-secondary mb-1">Data Health</p>
          <p className="text-2xl font-bold text-theme-text">{data.dataQuality.healthScore}%</p>
        </div>
      </div>

      {/* Data Quality Issues */}
      {data.dataQuality.issues.length > 0 && (
        <div className="bg-theme-bg rounded-lg p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-theme-warning" />
            Data Quality Issues
          </h3>
          <div className="space-y-2">
            {data.dataQuality.issues.map((issue, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-theme-bg-secondary rounded">
                <XCircle className="w-5 h-5 text-theme-danger mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-theme-text font-medium">
                    {issue.count.toLocaleString()} affected picks
                  </p>
                  <p className="text-sm text-theme-text-secondary">{issue.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-theme-border">
            <button
              type="button"
              onClick={() => onOpenPicksData()}
              className="text-sm text-theme-primary hover:underline"
            >
              → View detailed picks data to investigate these issues
            </button>
          </div>
        </div>
      )}

      {/* Calibration Status */}
      <div className="bg-theme-bg rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-theme-text">Calibration Status</h3>
          <div className={`flex items-center gap-2 ${statusInfo.color}`}>
            <StatusIcon className="w-5 h-5" />
            <span className="font-medium">{statusInfo.label}</span>
          </div>
        </div>

        {/* Prediction Calibration Table */}
        <h4 className="text-sm font-semibold text-theme-text-secondary mb-3 uppercase tracking-wide">
          Prediction Calibration
        </h4>
        <CalibrationTable buckets={data.calibration.prediction} />

        {/* Strategy Calibration Table */}
        {data.calibration.strategy.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-theme-text-secondary mb-1 uppercase tracking-wide">
              Strategy Calibration
            </h4>
            <p className="text-xs text-theme-text-tertiary mb-3">
              Based on{' '}
              {data.calibration.strategy.reduce((sum, b) => sum + b.picks, 0).toLocaleString()}{' '}
              picks with strategy scores (
              {data.overview.settledPicks > 0
                ? (
                    (data.calibration.strategy.reduce((sum, b) => sum + b.picks, 0) /
                      data.overview.settledPicks) *
                    100
                  ).toFixed(1)
                : '0.0'}
              % of settled)
            </p>
            <CalibrationTable buckets={data.calibration.strategy} />
          </div>
        )}
      </div>

      {/* Performance Trends */}
      <div className="bg-theme-bg rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">Performance Trends</h3>
        <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4">
          {data.trends.map((trend, idx) => (
            <div key={idx} className="bg-theme-bg-secondary rounded-lg p-4">
              <p className="text-sm text-theme-text-secondary mb-2">
                {trend.period === 'last_7d' && 'Last 7 Days'}
                {trend.period === 'last_30d' && 'Last 30 Days'}
                {trend.period === 'all_time' && 'All Time'}
              </p>
              <p className="text-2xl font-bold text-theme-text mb-1">
                {(trend.hitRate * 100).toFixed(1)}%
              </p>
              <div className="flex items-center justify-between">
                <p className="text-xs text-theme-text-tertiary">{trend.picks} picks</p>
                <p
                  className={`text-sm font-semibold ${trend.roi >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
                >
                  {trend.roi >= 0 ? '+' : ''}
                  {(trend.roi * 100).toFixed(1)}% ROI
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Analysis Mode Breakdown */}
      {data.breakdowns.byAnalysisMode.length > 0 && (
        <div className="bg-theme-bg rounded-lg p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4">Breakdown by Analysis Mode</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-theme-border">
                  <th className="text-left py-2 px-4 text-theme-text-secondary">Mode</th>
                  <th className="text-right py-2 px-4 text-theme-text-secondary">Picks</th>
                  <th className="text-right py-2 px-4 text-theme-text-secondary">Hit Rate</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const bestHitRate = Math.max(
                    ...data.breakdowns.byAnalysisMode.map((m) => m.hitRate),
                  );
                  return data.breakdowns.byAnalysisMode.map((item, idx) => {
                    const isBest = item.hitRate === bestHitRate;
                    return (
                      <tr
                        key={idx}
                        className="border-b border-theme-border hover:bg-theme-bg-secondary"
                      >
                        <td className="py-2 px-4 text-theme-text capitalize font-medium">
                          {item.mode}
                        </td>
                        <td className="text-right py-2 px-4 text-theme-text">
                          {item.picks.toLocaleString()}
                        </td>
                        <td
                          className={`text-right py-2 px-4 font-medium ${isBest ? 'text-theme-success' : 'text-theme-text'}`}
                        >
                          {(item.hitRate * 100).toFixed(1)}%
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confidence Breakdown */}
      {data.breakdowns.byConfidence.length > 0 && (
        <div className="bg-theme-bg rounded-lg p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4">Breakdown by Confidence</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-theme-border">
                  <th className="text-left py-2 px-4 text-theme-text-secondary">Confidence</th>
                  <th className="text-right py-2 px-4 text-theme-text-secondary">Picks</th>
                  <th className="text-right py-2 px-4 text-theme-text-secondary">Hit Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.breakdowns.byConfidence.map((item, idx) => {
                  const hitRateColor =
                    item.hitRate >= 0.55
                      ? 'text-theme-success'
                      : item.hitRate >= 0.5
                        ? 'text-theme-warning'
                        : 'text-theme-danger';
                  return (
                    <tr
                      key={idx}
                      className="border-b border-theme-border hover:bg-theme-bg-secondary"
                    >
                      <td className="py-2 px-4 text-theme-text font-medium">{item.range}</td>
                      <td className="text-right py-2 px-4 text-theme-text">
                        {item.picks.toLocaleString()}
                      </td>
                      <td className={`text-right py-2 px-4 font-medium ${hitRateColor}`}>
                        {(item.hitRate * 100).toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Prop Type Breakdown */}
      <div className="bg-theme-bg rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">Breakdown by Prop Type</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-theme-border">
                <th className="text-left py-2 px-4 text-theme-text-secondary">Prop Type</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Picks</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Hit Rate</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Avg Odds</th>
              </tr>
            </thead>
            <tbody>
              {data.breakdowns.byPropType.map((prop, idx) => (
                <tr
                  key={idx}
                  onClick={() => onOpenPicksData({ propType: [prop.propType] })}
                  className="border-b border-theme-border hover:bg-theme-bg-secondary cursor-pointer"
                >
                  <td className="py-2 px-4 text-theme-text">{formatStatLabel(prop.propType)}</td>
                  <td className="text-right py-2 px-4 text-theme-text">{prop.picks}</td>
                  <td className="text-right py-2 px-4 text-theme-text">
                    {(prop.hitRate * 100).toFixed(1)}%
                  </td>
                  <td className="text-right py-2 px-4 text-theme-text font-mono">
                    {formatAmericanOdds(prop.avgOdds) ?? MISSING_ODDS}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
